import React from 'react';
import OrganizationLink from './OrganizationLink';
import OrganizationService from '../../services/OrganizationService';
import TitanDataGrid from '../TitanDataGrid/TitanDataGrid';
import TitanConfirmationDialog from '../Dialog/TitanConfirmationDialog';
import { useTitan } from '../Titan/Titan';
import OrganizationDialog from './OrganizationDialog';
import ActionsMenu from '../Menu/ActionsMenu';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import useTitanDataGrid from '../TitanDataGrid/useTitanDataGrid';
import { useParams } from 'react-router-dom';

export default function OrganizationsManageList() {
  const [openOrganizationDialog, setOpenOrganizationDialog] = React.useState(
    false
  );
  const [dialogMode, setDialogMode] = React.useState('create');
  const [editableOrganization, setEditableOrganization] = React.useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const { pushSnackbar } = useTitan();

  const { page: pageParam = 0 } = useParams();

  const loadData = React.useCallback(async (query, config) => {
    const params = {
      ...query
    };

    return OrganizationService.getOrganizations(params, config);
  }, []);

  const gridOptions = {};

  if (pageParam && Number(pageParam) > 0) {
    gridOptions.page = Number(pageParam);
  }

  const titanDataGridProps = useTitanDataGrid(loadData, gridOptions);

  const { setRows } = titanDataGridProps;

  const onSaveDialog = organization => {
    if (dialogMode === 'create') {
      setRows(prev => [organization, ...prev]);
    } else {
      setRows(prev =>
        prev.map(o => (o.id === organization.id ? organization : o))
      );
    }
  };

  const onCloseDialog = () => {
    setOpenOrganizationDialog(false);
  };

  const onDelete = async () => {
    await OrganizationService.deleteOrganization(editableOrganization.id);

    pushSnackbar('Organization successfully deleted', { variant: 'success' });
    setOpenDeleteDialog(false);
    setRows(prev => prev.filter(o => o.id !== editableOrganization.id));
  };

  const columns = React.useMemo(
    () => [
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => <OrganizationLink organization={row} />
      },
      {
        headerName: '# Users',
        field: 'countUsers',
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) =>
          row && row.countMembers ? row.countMembers : 0
      },
      {
        headerName: '# Build Modules',
        field: 'countBuildModules',
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) =>
          row && row.countBuildModules ? row.countBuildModules : 0
      },
      {
        headerName: '# Fusion Modules',
        field: 'countFusionModules',
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) =>
          row && row.countFusionModules ? row.countFusionModules : 0
      },
      {
        headerName: '',
        field: 'actions',
        width: 60,
        sortable: false,
        renderCell: ({ row }) => (
          <ActionsMenu
            items={[
              {
                title: 'Edit',
                icon: EditIcon,
                onClick: () => {
                  setEditableOrganization(row);
                  setDialogMode('edit');
                  setOpenOrganizationDialog(true);
                }
              },
              {
                title: 'Delete',
                icon: DeleteIcon,
                onClick: () => {
                  setEditableOrganization(row);
                  setOpenDeleteDialog(true);
                }
              }
            ]}
          />
        )
      }
    ],
    []
  );

  return (
    <>
      <TitanDataGrid
        {...titanDataGridProps}
        title="Organizations"
        columns={columns}
        onCreateClick={() => {
          setEditableOrganization({});
          setDialogMode('create');
          setOpenOrganizationDialog(true);
        }}
        createButtonLabel="Organization"
      />

      {openOrganizationDialog && (
        <OrganizationDialog
          organization={editableOrganization}
          mode={dialogMode}
          onClose={() => onCloseDialog()}
          onSave={onSaveDialog}
        />
      )}

      {openDeleteDialog && (
        <TitanConfirmationDialog
          title="Delete organization?"
          message={`Are you sure you want to delete organization ${
            editableOrganization.name
          }?`}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={() => onDelete()}
        />
      )}
    </>
  );
}
